import { useQuery, useMutation } from "@apollo/client";
import { useMemo, useEffect, useRef } from "react";
import { GET_TIMELINE_EDITOR } from "MVVM/GraphQL/timelineEditor";
import { MOMENT_CREATE_CLIP } from "MVVM/GraphQL/mutations/momentRequestClip";
import { analytics } from "MVVM/Analytics";

export interface TimelineEditorHeader {
  mapName: string;
  tickRate: number;
  totalTicks: number;
}

export interface TimelineEditorPlayer {
  steamId: string;
  name: string;
  observerSlot: number;
  avatar?: string;
}

export interface TimelineEditorKillerData {
  weapon: string;
  headShot: boolean;
  team: string;
}

export interface TimelineEditorKill {
  tick: number;
  killer: string;
  victim: string;
  location: string;
  killerData: TimelineEditorKillerData;
}

export interface TimelineEditorBomb {
  tick: number;
  type: string;
  site: string;
  initiator: string;
}

export interface TimelineEditorRound {
  round: number;
  startTick: number;
  endTick: number;
  winner: string;
  terroristsScore: number;
  counterTerroristsScore: number;
  kills: TimelineEditorKill[];
  bomb: TimelineEditorBomb[];
}

export interface TimelineEditorData {
  header: TimelineEditorHeader;
  players: TimelineEditorPlayer[];
  rounds: TimelineEditorRound[];
}

export interface ReplayCommand {
  tick: number;
  class: string;
  command?: string;
  playerSlot?: number;
  gotoTick?: number;
  steamId64?: string;
}

interface TimelineEditorQueryResult {
  timelineEditor: TimelineEditorData;
}

interface MomentCreateClipResult {
  momentCreateClip: {
    success: boolean;
    errors: Array<{
      field: string;
      code: string;
      message: string;
    }>;
    moments: Array<{
      id: string;
    }>;
  };
}

interface TimelineEditorViewModelProps {
  matchId: string;
}

const TimelineEditorViewModel = ({ matchId }: TimelineEditorViewModelProps) => {
  const activeMatchId = matchId;
  const pageLoadTime = useRef<number>(Date.now());
  const hasTrackedPageLoad = useRef<boolean>(false);
  const hasTrackedLoadResult = useRef<boolean>(false);
  const checkEligibility = process.env.REACT_APP_LIMIT_ENFORCED === "true";

  const { data, loading, error } = useQuery<TimelineEditorQueryResult>(
    GET_TIMELINE_EDITOR,
    {
      variables: { matchId: activeMatchId },
      fetchPolicy: "cache-and-network",
      skip: !activeMatchId,
    },
  );

  const [createClip, { loading: createClipLoading, error: createClipError }] =
    useMutation<MomentCreateClipResult>(MOMENT_CREATE_CLIP);

  const processedData = useMemo(() => {
    if (!data?.timelineEditor) return null;

    const uniquePlayers = data.timelineEditor.players.reduce((acc, player) => {
      if (!acc.some((p) => p.steamId === player.steamId)) {
        acc.push(player);
      }
      return acc;
    }, [] as TimelineEditorPlayer[]);

    return {
      ...data.timelineEditor,
      players: uniquePlayers,
    };
  }, [data]);

  useEffect(() => {
    if (processedData && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true;
      pageLoadTime.current = Date.now();
    }
  }, [processedData]);

  useEffect(() => {
    if (hasTrackedLoadResult.current || loading) return;

    if (error) {
      hasTrackedLoadResult.current = true;
      analytics.track("Timeline Editor - Load Error", {
        matchId: activeMatchId,
        errorMessage: error.message,
      });
    } else if (processedData) {
      hasTrackedLoadResult.current = true;
      analytics.track("Timeline Editor - Load Success", {
        matchId: activeMatchId,
        playerCount: processedData.players?.length || 0,
        roundCount: processedData.rounds?.length || 0,
      });
    }
  }, [loading, error, processedData, activeMatchId]);

  const submitReplaySequence = async (
    replaySequence: ReplayCommand[],
    title?: string,
    analyticsData?: {
      segmentCount: number;
    },
  ) => {
    const timeOnPageSeconds = Math.round(
      (Date.now() - pageLoadTime.current) / 1000,
    );

    analytics.track("Timeline Editor - Clip Requested", {
      matchId: activeMatchId,
      segmentCount: analyticsData?.segmentCount || 0,
      replayCommandCount: replaySequence.length,
    });

    analytics.track("Timeline Editor - Time On Page", {
      matchId: activeMatchId,
      timeOnPageSeconds,
    });

    try {
      const result = await createClip({
        variables: {
          momentCreateClipInput: {
            matchId: activeMatchId,
            checkEligibility,
            replaySequence,
            ...(title && {
              effectsOverrides: {
                title,
              },
            }),
          },
        },
      });

      return {
        success: result.data?.momentCreateClip?.success || false,
        errors: result.data?.momentCreateClip?.errors || [],
        moments: result.data?.momentCreateClip?.moments || [],
      };
    } catch (err) {
      return {
        success: false,
        errors: [{ field: "general", code: "ERROR", message: String(err) }],
        moments: [],
      };
    }
  };

  return {
    data: processedData,
    loading,
    error,
    submitReplaySequence,
    createClipLoading,
    createClipError,
  };
};

export { TimelineEditorViewModel };
